import { Extension } from "@37signals/lexxy";
import { $applyNodeReplacement, $getSelection, ElementNode, defineExtension } from "lexical"

/*
Sequence (Lexxy + Lexical):
1) User clicks alignment button in Lexxy toolbar.
2) #applyAlignment() runs inside editor.update().
3) Selected linked image nodes get node.alignment updated.
4) Their top-level block is wrapped (or updated) with AlignmentWrapperNode.
5) #dispatchAlignmentCommand() also sends FORMAT_ELEMENT_COMMAND for normal block alignment.
6) Lexical renders/exportDOM/importDOM preserve wrapper via data-mstn-alignment-* attributes.
*/

const ALIGNMENTS = [
  { name: "left", label: "Align Left" },
  { name: "center", label: "Align Center" },
  { name: "right", label: "Align Right" }
]

// Restricts alignment values to supported options used by wrapper and export paths.
function normalizeAlignment(alignment) {
  if (alignment === "center" || alignment === "right") return alignment
  return "left"
}

// Reads alignment from persisted wrapper attributes/style when importing DOM back into Lexical.
function readAlignmentFromElement(element) {
  return normalizeAlignment(
    element.getAttribute("data-mstn-alignment") ||
    element.getAttribute("data-alignment") ||
    element.getAttribute("alignment") ||
    element.style?.textAlign
  )
}

// Factory helper to construct a registered AlignmentWrapperNode instance.
function $createAlignmentWrapperNode(alignment = "left") {
  return $applyNodeReplacement(new AlignmentWrapperNode(alignment))
}

class AlignmentWrapperNode extends ElementNode {
  // Stable node type key used by Lexical registration and serialization.
  static getType() {
    return "alignment_wrapper"
  }

  // Required by Lexical immutable node updates.
  static clone(node) {
    return new AlignmentWrapperNode(node.__alignment, node.__key)
  }

  // Restores wrapper node from Lexical JSON editor state.
  static importJSON(serializedNode) {
    return $createAlignmentWrapperNode(serializedNode.alignment)
  }

  // Rehydrates wrapper node from HTML DOM only when the marker attribute is present.
  static importDOM() {
    return {
      div: (domNode) => {
        if (!(domNode instanceof HTMLElement)) return null
        if (domNode.getAttribute("data-mstn-alignment-wrapper") !== "true") return null

        return {
          conversion: (element) => {
            return { node: $createAlignmentWrapperNode(readAlignmentFromElement(element)) }
          },
          priority: 2
        }
      }
    }
  }

  // Initialize a wrapper node.
  constructor(alignment = "left", key) {
    super(key)
    this.__alignment = normalizeAlignment(alignment)
  }

  // Builds live editor DOM for this wrapper node.
  createDOM() {
    const element = document.createElement("div")
    element.style.textAlign = this.__alignment
    element.style.alignItems = this.__alignment
    element.setAttribute("data-mstn-alignment-wrapper", "true")
    element.setAttribute("data-mstn-alignment", this.__alignment)
    return element
  }

  // Keeps the existing wrapper DOM in sync when alignment changes.
  updateDOM(prevNode, dom) {
    if (prevNode.__alignment !== this.__alignment) {
      dom.style.textAlign = this.__alignment
      dom.style.alignItems = this.__alignment
      dom.setAttribute("data-mstn-alignment", this.__alignment)
    }

    if (dom.getAttribute("data-mstn-alignment-wrapper") !== "true") {
      dom.setAttribute("data-mstn-alignment-wrapper", "true")
    }

    return false
  }

  // Exports wrapper as HTML with marker attributes so importDOM can recognize it later.
  exportDOM() {
    const element = document.createElement("div")
    element.style.textAlign = this.__alignment
    element.style.alignItems = this.__alignment
    element.setAttribute("data-mstn-alignment-wrapper", "true")
    element.setAttribute("data-mstn-alignment", this.__alignment)

    return { element }
  }

  // Persists wrapper node in Lexical JSON state.
  exportJSON() {
    return {
      ...super.exportJSON(),
      type: "alignment_wrapper",
      version: 1,
      alignment: this.__alignment
    }
  }

  // Mutates writable state inside editor.update(). Used by toolbar.
  setAlignment(alignment) {
    this.getWritable().__alignment = normalizeAlignment(alignment)
  }
}

// Type guard for wrapper-node checks before wrapping/re-wrapping blocks.
function $isAlignmentWrapperNode(node) {
  return node instanceof AlignmentWrapperNode
}

// Limits this behavior to linked image attachments.
function isAlignableAttachmentNode(node) {
  return node.getType?.() === "linked_image_attachment"
}

export class AlignmentExtension extends Extension {
  // Enables extension only for rich-text capable editor instances.
  get enabled() {
    return this.editorElement.supportsRichText
  }

  // Registers the custom wrapper node with Lexical.
  get lexicalExtension() {
    return defineExtension({
      name: "mstone/alignment-wrapper",
      nodes: [AlignmentWrapperNode]
    })
  }

  // Builds and inserts the alignment dropdown into the Lexxy toolbar.
  initializeToolbar(toolbar) {
    const dropdown = this.#initDropdown()
    const trigger = this.#initTrigger()
    const panel = this.#initPanel()

    dropdown.appendChild(trigger)

    for (const alignment of ALIGNMENTS) {
      let button = this.#buildButton(alignment)

      button.addEventListener("click", () => {
        this.#applyAlignment(alignment.name)
        dropdown.close()
      })

      panel.appendChild(button)
    }

    dropdown.appendChild(panel)

    // Insert before the undo button (push-right area)
    const undoBtn = toolbar.querySelector("button[name='undo']")
    if (undoBtn) {
      undoBtn.before(dropdown)
    } else {
      toolbar.appendChild(dropdown)
    }
  }

  // Creates the <lexxy-toolbar-dropdown> container.
  #initDropdown() {
    let dropdown = document.createElement("lexxy-toolbar-dropdown")

    dropdown.className = [
      "lexxy-editor__toolbar-dropdown",
      "lexxy-editor__toolbar-dropdown--chevron",
      "mstn-alignment-dropdown",
    ].join(' ')

    dropdown.dataset['mstnName'] = "alignment"

    return dropdown
  }

  // Creates the clickable dropdown trigger button.
  #initTrigger() {
    let trigger = document.createElement("button")
    trigger.className = "lexxy-editor__toolbar-button lexxy-editor__toolbar-button--chevron"
    trigger.type = "button"
    trigger.setAttribute("data-dropdown-trigger", "")
    trigger.setAttribute("name", "text-align")
    trigger.setAttribute("title", "Align Text")
    trigger.setAttribute("aria-haspopup", "menu")
    trigger.setAttribute("aria-expanded", "false")
    trigger.innerHTML = `<i class="fa-solid fa-align-left"></i>`

    return trigger
  }

  // Creates the dropdown panel container.
  #initPanel() {
    let panel = document.createElement("div")
    panel.setAttribute("data-dropdown-panel", "")
    panel.setAttribute("role", "menu")
    panel.hidden = true
    panel.className = "lexxy-editor__toolbar-dropdown-list"

    return panel
  }

  // Creates one alignment option button.
  #buildButton(alignment) {
    let icon = this.#buildIcon(`fa-solid fa-align-${alignment.name}`)
    let button = document.createElement("button")
    button.type = "button"
    button.setAttribute("name", `align-${alignment.name}`)
    button.setAttribute("title", alignment.label)

    let label = document.createElement("span")
    label.className = "text-sm"
    label.textContent = alignment.label

    button.appendChild(icon)
    button.appendChild(label)

    return button
  }

  // Creates an icon element used by toolbar buttons.
  #buildIcon(className) {
    let icon = document.createElement("i")
    icon.className = className

    return icon
  }

  // Applies alignment to selected linked-image nodes and wraps their top-level blocks if needed.
  #applyAlignment(alignment) {
    this.#handleAttachmentNodes(alignment)
    this.#handleBlockElements(alignment)
  }

  #handleAttachmentNodes(alignment) {
    this.editorElement.editor.update(() => {
      const selection = $getSelection()
      if (!selection || typeof selection.getNodes !== "function") return

      const nodes = selection.getNodes()
      const topLevelBlocks = new Set()

      nodes.forEach((node) => {
        if (isAlignableAttachmentNode(node) && typeof node.getWritable === "function") {
          node.getWritable().alignment = alignment
          topLevelBlocks.add(node.getTopLevelElementOrThrow())
        }
      })

      topLevelBlocks.forEach((block) => {
        if (!block || block.getType?.() === "root") return

        if ($isAlignmentWrapperNode(block)) {
          block.setAlignment(alignment)
          return
        }

        const parent = block.getParent()
        if ($isAlignmentWrapperNode(parent)) {
          parent.setAlignment(alignment)
          return
        }

        const wrapper = $createAlignmentWrapperNode(alignment)
        block.insertBefore(wrapper)
        wrapper.append(block)
      })
    })
  }

  // Dispatches Lexical's built-in element-format command for standard block alignment behavior.
  #handleBlockElements(alignment) {
    const editor = this.editorElement.editor
    const cmd = this.#findCommand(editor, "FORMAT_ELEMENT_COMMAND")
    if (cmd) { editor.dispatchCommand(cmd, alignment) }
  }

  // Finds Lexical's internal format command object so this extension can dispatch it.
  #findCommand(editor, typeName) {
    for (const [cmd] of editor._commands) {
      if (cmd.type === typeName) return cmd
    }
    return null
  }
}
